import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { action, can, defineScreen } from '@/shared/screen/define';

import { preparedAction } from './beneficiary-limits.screen';
import { BANK_ACCOUNT_LIST_PATH } from './bank-account.search';

export const ACCOUNT_MED_BASE = `${BANK_ACCOUNT_LIST_PATH}/$bankAccountId/pix/pix-med`;
export const accountMedPath = (id: string) =>
  `${BANK_ACCOUNT_LIST_PATH}/${encodeURIComponent(id)}/pix/pix-med`;
export const accountMedSearch = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  size: z.coerce.number().int().min(1).max(100).optional().catch(undefined),
  status: z.string().optional().catch(undefined),
});
export const medContest = preparedAction(
  'bankAccount.med.contest',
  msg`Contestar transação`,
  'write',
  msg`Abre a escolha do motivo para o pagamento aberto. A pessoa confere e confirma com senha e código MFA. Nenhuma contestação é enviada pelo assistente.`,
  msg`Contestar transação`,
);
export const medCancel = preparedAction(
  'bankAccount.med.cancel',
  msg`Cancelar contestação`,
  'destructive',
  msg`Abre a confirmação do cancelamento da contestação aberta. A pessoa precisa confirmar; o assistente não cancela nada.`,
  msg`Cancelar contestação`,
);
export const medOpen = action({
  id: 'bankAccount.med.open',
  kind: 'read',
  label: msg`Ver detalhes`,
  description: msg`Abre os detalhes da linha escolhida na página atual.`,
  input: z.object({ id: z.string() }),
  run: () => undefined,
});
export const medPage = action({
  id: 'bankAccount.med.page',
  kind: 'navigate',
  label: msg`Mudar página`,
  description: msg`Muda a página ou a quantidade de itens exibidos.`,
  input: z.object({ page: z.number().int().min(0), size: z.number().int().min(1).max(100) }),
  run: () => undefined,
});
const access = { anyOf: [can('BankAccount', 'Read'), can('BankAccount', 'ViewTransitoryAccount')] };
export const bankAccountMedScreen = defineScreen({
  id: 'bankAccount.med',
  path: ACCOUNT_MED_BASE,
  title: msg`Pix MED`,
  description: msg`Atalhos para contestar transações Pix, consultar contestações e ver informações sobre o MED.`,
  access,
  place: { icon: 'wallet', section: 'bank-account', record: msg`Pix MED` },
  actions: [],
});
export const bankAccountMedContestScreen = defineScreen({
  id: 'bankAccount.med.contestations',
  path: `${ACCOUNT_MED_BASE}/contestar`,
  title: msg`Contestar uma transação`,
  description: msg`Lista os pagamentos Pix da conta para consultar seus detalhes e solicitar uma contestação.`,
  access,
  place: { icon: 'wallet', parent: 'bankAccount.med', record: msg`Contestar` },
  actions: [medPage, medContest],
});
export const bankAccountMedConsultScreen = defineScreen({
  id: 'bankAccount.med.recoveries',
  path: `${ACCOUNT_MED_BASE}/consultar`,
  title: msg`⁠Consultar contestações`,
  description: msg`Histórico e status das contestações da conta, com detalhes e cancelamento das solicitações que ainda permitem cancelar.`,
  access,
  place: { icon: 'wallet', parent: 'bankAccount.med', record: msg`Consultar` },
  actions: [medPage, medCancel],
});
export const bankAccountMedInformationScreen = defineScreen({
  id: 'bankAccount.med.information',
  path: `${ACCOUNT_MED_BASE}/informacoes`,
  title: msg`Informações sobre o MED`,
  description: msg`Mostra a mensagem sobre a futura disponibilidade de informações detalhadas do MED.`,
  access,
  place: { icon: 'wallet', parent: 'bankAccount.med', record: msg`Informações` },
  actions: [],
});
export const ACCOUNT_MED_SCREENS = [
  bankAccountMedScreen,
  bankAccountMedContestScreen,
  bankAccountMedConsultScreen,
  bankAccountMedInformationScreen,
];
